import React, { Dispatch, useMemo } from 'react';
import DialogFrame from '../_parts/DialogFrame';
import DialogHeader from '../_parts/DialogHeader';
import Documents from '../_parts/Documents';
import CtaButton from '../_parts/CtaButton';
import UploadFile from '../_parts/UploadFile';
import Message from '../_parts/Message';
import { DialogActions, DialogContent, Divider, FormLabel, Stack } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    addDocumentFiles,
    showAddDocumentFilesDialog,
} from '../../../../redux/general/sliceFileUpload';
import { IAppReduxStore } from '../../../../redux/store';
import { headingFontBold, vars } from '../../../../Theme';
import { mdiInformation } from '@mdi/js';
import { useParams } from 'react-router-dom';
import { getFileExtensionsFromFormats } from '../../../../util/FileUtils';
import { defaultAllowedDataModelFileFormat, defaultAllowedFileFormat } from '../../../../Constants';
import {
    appendFiles,
    getMappedFilesToRelatedDocuments,
    removeFile,
    resetFileState,
} from '../../../../redux/import/sliceImportFileUpload';

const labelSx = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'AddDocumentFilesDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const { replicaId } = useParams();

    const {
        loading: addFilesPending,
        config: { universalId, docTypeKey },
    } = useSelector((_store: IAppReduxStore) => _store.fileUpload.addDocumentFilesDialogState);
    const { data: documents, loading: documentInfoLoading } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );
    const { data: docTypes, docTypeInfoState: doctypeInfoLoading } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocTypes,
    );
    const uploaded = useSelector(getMappedFilesToRelatedDocuments);

    const { files, fileDataModels } = useMemo(
        () => ({
            files: uploaded.filter(x => x.universalId.startsWith('F-')),
            fileDataModels: uploaded.filter(x => x.universalId.startsWith('D-')),
        }),
        [uploaded],
    );

    const {
        noneAllowed,
        filesAllowed,
        dataModelsAllowed,
        extensionsAllowed,
        extensionsDataModelAllowed,
    } = useMemo(() => {
        const doctype = docTypes.find(x => x.name === docTypeKey);
        const filesAllowed = doctype?.additionalInfo?.fileAttachmentsRule === 'multiple';
        const dataModelsAllowed = doctype?.additionalInfo?.fileDataModelRule === 'multiple';
        const extensionsAllowed = getFileExtensionsFromFormats(
            doctype?.extendedConfig?.AllowedFileFormats?.split('|') ?? [defaultAllowedFileFormat],
        ).join(',');

        const extensionsDataModelAllowed = getFileExtensionsFromFormats(
            doctype?.extendedConfig?.AllowedDataModelFileFormats?.split('|') ?? [
                defaultAllowedDataModelFileFormat,
            ],
        ).join(',');
        return {
            noneAllowed: !filesAllowed && !dataModelsAllowed,
            filesAllowed,
            dataModelsAllowed,
            extensionsAllowed,
            extensionsDataModelAllowed,
        };
    }, [docTypes, doctypeInfoLoading]);

    const onClose = () => {
        dispatch(showAddDocumentFilesDialog(null));
        dispatch(resetFileState());
    };

    const onConfirm = () => {
        dispatch(
            addDocumentFiles({
                replicaId,
                universalId,
                docTypeKey,
                files: files.map(x => x.universalId.slice(2)),
                fileDataModels: fileDataModels.map(x => x.universalId.slice(2)),
            }),
        );
        onClose();
    };

    return (
        <DialogFrame open>
            <DialogHeader dialogTitle={t('title')} onClose={onClose} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={
                        documentInfoLoading || doctypeInfoLoading === 'Pending' || addFilesPending
                    }
                    documentSelection={<Documents documents={documents} />}
                    main={
                        <Stack spacing='12px'>
                            {filesAllowed && (
                                <>
                                    <FormLabel sx={labelSx}>{t('labelAttached')}</FormLabel>
                                    <UploadFile
                                        idPrefix='F-'
                                        hideEmptyList={true}
                                        allowMultiple={true}
                                        buttonLabel={t('labelAddFiles')}
                                        acceptFileTypes={extensionsAllowed}
                                        appendFilesInfo={appendFiles}
                                        removeFile={removeFile}
                                        uploadedDocuments={files}
                                        documentsLabel={t('labelFiles', {
                                            count: files.length,
                                        })}
                                    />
                                </>
                            )}
                            {dataModelsAllowed && (
                                <>
                                    <FormLabel sx={labelSx}>{t('labelModels')}</FormLabel>
                                    <UploadFile
                                        idPrefix='D-'
                                        hideEmptyList={true}
                                        allowMultiple={true}
                                        buttonLabel={t('labelAddFiles')}
                                        acceptFileTypes={extensionsDataModelAllowed}
                                        appendFilesInfo={appendFiles}
                                        removeFile={removeFile}
                                        uploadedDocuments={fileDataModels}
                                        documentsLabel={t('labelFiles', {
                                            count: fileDataModels.length,
                                        })}
                                    />
                                </>
                            )}
                            {noneAllowed && (
                                <Message text={t('infoNotAllowed')} icon={mdiInformation} />
                            )}
                        </Stack>
                    }
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ paddingRight: '24px' }}>
                <CtaButton
                    text={t(noneAllowed ? 'ok' : 'cancel')}
                    variant='text'
                    onClick={onClose}
                />
                {!noneAllowed && (
                    <CtaButton
                        disabled={!files?.length && !fileDataModels?.length}
                        text={t('confirm')}
                        variant='contained'
                        onClick={onConfirm}
                    />
                )}
            </DialogActions>
        </DialogFrame>
    );
};

export interface IAddDocumentFilesDialogState {
    config: {
        universalId: string;
        docTypeKey: string;
    } | null;
    loading: boolean;
    result: number | null;
    error: { count: number; total: number } | null;
}
